// src/components/hub/work/hooks/useWorkDetail.js
import { useCallback, useEffect, useState } from "react";
import {
  getWorkDetail,
  moveWork,
  updateWork,
  deleteWork,
  archiveWork,
} from "../../../../api/hub/work";

// Detects the fn_work_delete trigger notice ("this work was deleted …" /
// "already deleted …"). This trigger fires AFTER the soft-delete succeeds, so
// the row IS gone from the DB — callers should treat this as a successful
// delete/archive for cache-cleanup purposes.
const isAlreadyDeletedError = (err) => /already deleted|this work was deleted/i.test(err?.message || "");

// Owns the slide-out's detail state and every inline-edit / delete / archive
// handler for a single work item. Handlers keep the same names and signatures
// the work page used before extraction, so call sites are unchanged.
const useWorkDetail = ({
  selectedTaskId,
  setSelectedTask,
  includeDone,
  patchItem,
  removeItem,
  setRetryNonce,
  setSubtaskRevision,
  setDebouncedSearch,
  setSearchQuery,
  showUndoToast,
  toastError,
  toastSuccess,
}) => {
  const initialState = { loading: false, error: "", data: null };
  const [detail, setDetail] = useState(initialState);

  // ─── Load task detail ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedTaskId) { setDetail(initialState); return; }
    let cancelled = false;
    setDetail((prev) => ({ ...prev, loading: true, error: "" }));
    getWorkDetail(selectedTaskId)
      .then((resp) => {
        if (!cancelled) setDetail({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((e) => {
        if (!cancelled) setDetail({ loading: false, error: e?.message || "Failed to load task", data: null });
      });
    return () => { cancelled = true; };
  }, [selectedTaskId]); // Items 2 & 3: retryNonce removed from detail deps.
  // Bumping retryNonce (after any write) used to refetch detail here, which set
  // loading:true and made TaskSlideOut flash its skeleton (unmounting WorkPanel).
  // The list/bucket cache still refreshes on retryNonce via useWorkBucketCache;
  // writes that need fresh server-side detail fields call refreshDetailSilent().

  // ─── Silent detail refresh (Items 2 & 3) ─────────────────────────────────
  // Re-fetches fn_work_detail and swaps detail.data WITHOUT ever setting
  // loading:true. Writes that need the server's fresh fields (work type's new
  // pipeline status, the full detail after a subtask/attachment/message change)
  // call this instead. Errors are logged, not surfaced — the slide-out keeps
  // its current (still-valid) data rather than flashing an error state.
  const refreshDetailSilent = useCallback(async () => {
    if (!selectedTaskId) return;
    try {
      const resp = await getWorkDetail(selectedTaskId);
      setDetail({ loading: false, error: "", data: resp?.data ?? null });
    } catch (e) {
      console.error("Silent detail refresh failed:", e);
    }
  }, [selectedTaskId]);

  // ─── Position move ──────────────────────────────────────────────────────────
  const handlePositionClick = useCallback(async (taskId, newStatusCode, label) => {
    if (!taskId || !newStatusCode) return;
    // fn_work_move returns the move outcome as { id, type, closed, status }
    // (wrapped by moveWork under resp.data — see src/api/hub/work.js).
    // `closed: true` means the task landed in a terminal "done"/"canceled"
    // position (type "closed"). Whether that task should stay visible depends
    // on the "Include done" filter (Item 1):
    //   - includeDone OFF → fn_work_list excludes done tasks (p_include_done
    //     defaults to false), so drop the row from every cached bucket now.
    //     It disappears instantly without a page refresh.
    //   - includeDone ON → done tasks ARE shown, so keep the row and patch its
    //     status in place (the pill updates, the task stays visible).
    // A non-closed move (in_progress, needs_assistance, …) always patches in
    // place so the status pill updates and the task stays visible in its bucket.
    try {
      const resp = await moveWork(taskId, newStatusCode);
      const isClosed = resp?.data?.closed === true;
      if (isClosed && !includeDone) {
        // Include-done is OFF → done tasks are filtered out of the default
        // list, so drop the row from every cached bucket now.
        removeItem(taskId);
      } else {
        // Either the move wasn't terminal, OR "Include done" is ON — in both
        // cases the task stays visible. Reset days_in_status to 0 since the
        // task just entered this status; the server's true value (from
        // fn_work_status's append-only status ledger) reconciles on the next
        // full refetch.
        patchItem(taskId, { status: newStatusCode, days_in_status: 0 });
      }
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, status: newStatusCode } } };
      });
      toastSuccess(`Moved to ${label || newStatusCode}`, taskId, "status_change");
    } catch (e) { toastError(e?.message || "Move failed", taskId, "status_change"); }
  }, [patchItem, removeItem, includeDone, toastError, toastSuccess]);

  // ─── Title inline edit ───────────────────────────────────────────────────────
  const handleTitleChange = useCallback(async (taskId, newTitle) => {
    if (!taskId || !newTitle) return;
    try {
      await updateWork({ task_id: taskId, title: newTitle });
      patchItem(taskId, { title: newTitle });
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, title: newTitle } } };
      });
      toastSuccess("Title updated", taskId, "title_change");
    } catch (e) { toastError(e?.message || "Update failed", taskId, "title_change"); }
  }, [patchItem, toastError]);

  // ─── Priority / service inline edits (slide-out pills) ──────────────────────
  // Same in-place patch pattern as handleTitleChange: updateWork → patch the
  // bucketCache item + the detail.task, then toast. No retryNonce (no re-fetch).
  // priority "none" is sent as-is to clear the priority (matches saveEdit).
  const handlePriorityChange = useCallback(async (taskId, newPriority) => {
    if (!taskId) return;
    try {
      await updateWork({ task_id: taskId, priority: newPriority });
      patchItem(taskId, { priority: newPriority });
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, priority: newPriority } } };
      });
      toastSuccess(newPriority && newPriority !== "none" ? `Priority set to ${newPriority}` : "Priority cleared", taskId, "priority_change");
    } catch (e) { toastError(e?.message || "Update failed", taskId, "priority_change"); }
  }, [patchItem, toastSuccess, toastError]);

  // ─── Service inline edit ──────────────────────────────────────────────────────
  // Mirrors handleWorkTypeChange / handleClientChange (Item 2). The picker sends
  // the service CODE (opt.code) as `newService`. The slider's service pill in
  // TaskHeader reads TWO task fields: task.service (the label, which becomes the
  // code after a change — see getServiceLabel) AND task.service_code (the pill's
  // colour via getServiceColor, plus the `serviceValue && task.service_code`
  // render gate). Patching only task.service left task.service_code stale — so a
  // task that had no service kept service_code null and the gate fell through to
  // the "Service" placeholder, and a task switching service kept the old colour.
  // Patch BOTH fields with the code for instant feedback (the colour is correct
  // because service_code = code; the label resolves via getServiceLabel). The
  // server stores the human label in task.service and the code in task.service_code,
  // so refreshDetailSilent reconciles them without a skeleton flash — no
  // retryNonce bump, since a service change does not move the task between
  // buckets/pipelines (unlike work-type / client changes).
  const handleServiceChange = useCallback(async (taskId, newService) => {
    if (!taskId) return;
    try {
      await updateWork({ task_id: taskId, service: newService });
      patchItem(taskId, { service: newService, service_code: newService });
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, service: newService, service_code: newService } } };
      });
      // Reconcile the server's canonical service fields silently — the server
      // stores a human label in task.service and the code in task.service_code;
      // the local patch above writes the code into both for instant feedback.
      refreshDetailSilent();
      toastSuccess("Service updated", taskId, "service_change");
    } catch (e) { toastError(e?.message || "Update failed", taskId, "service_change"); }
  }, [patchItem, toastSuccess, toastError, refreshDetailSilent]);

  // ─── Work type / client inline edits (slide-out — Item 2) ───────────────────
  // Same in-place patch pattern as priority/service above, but both bump
  // retryNonce: a work type change can move the task to a different pipeline,
  // and a client change moves it to a different client grouping, so the bucket
  // cache must re-fetch to re-seat the card. patchItem gives instant feedback
  // on the id/code; retryNonce reconciles list position and the name-bearing
  // display fields (task.client / the work-type label) once the bucket reloads.
  const handleWorkTypeChange = useCallback(async (taskId, newWorkType) => {
    if (!taskId) return;
    try {
      await updateWork({ task_id: taskId, work_type: newWorkType });
      patchItem(taskId, { work_type: newWorkType });
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, work_type: newWorkType } } };
      });
      // Reconcile the new pipeline status silently — a work-type change may
      // move the task to the new type's first open status, which only the
      // server knows. No skeleton flash (refreshDetailSilent never sets
      // loading:true); the list refreshes via retryNonce below.
      refreshDetailSilent();
      toastSuccess("Work type updated", taskId, "work_type_change");
      setRetryNonce((n) => n + 1);
    } catch (e) { toastError(e?.message || "Update failed", taskId, "work_type_change"); }
  }, [patchItem, setRetryNonce, toastSuccess, toastError, refreshDetailSilent]);

  const handleClientChange = useCallback(async (taskId, newClientId) => {
    if (!taskId) return;
    try {
      await updateWork({ task_id: taskId, client_id: newClientId });
      // Optimistic patch. When SETTING a client, only client_id (an id) is
      // known locally — task.client (the human label) comes from the server
      // via refreshDetailSilent below. When CLEARING (newClientId === null),
      // also clear client (the display name) so the slide-out header shows
      // "Internal" immediately instead of waiting for the server refresh.
      // Without this, the header checks task.client (a name, not the id) and
      // sees the old name — the click appears to do nothing.
      const patch = newClientId == null ? { client_id: null, client: null } : { client_id: newClientId };
      patchItem(taskId, patch);
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, ...patch } } };
      });
      // Reconcile the client DISPLAY NAME silently. The local setDetail patch
      // above only updates client_id (an id, not a name) — task.client (the
      // human label) only comes from the server. Since retryNonce no longer
      // refetches detail (Items 2 & 3 fix), refreshDetailSilent pulls the
      // fresh name without a skeleton flash; the list refreshes via retryNonce.
      refreshDetailSilent();
      toastSuccess("Client updated", taskId, "client_change");
      setRetryNonce((n) => n + 1);
    } catch (e) { toastError(e?.message || "Update failed", taskId, "client_change"); }
  }, [patchItem, setRetryNonce, toastSuccess, toastError, refreshDetailSilent]);

  const handleSlideOutDueDateChange = useCallback(async (taskId, newDate) => {
    if (!taskId) return;
    try {
      const payload = { task_id: taskId };
      if (newDate) payload.due = newDate;
      else payload.clear_due = true;
      await updateWork(payload);
      const dueValue = newDate || null;
      patchItem(taskId, { due: dueValue });
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, due: dueValue } } };
      });
      toastSuccess(newDate ? "Due date updated" : "Due date cleared", taskId, "due_date_change");
    } catch (e) { toastError(e?.message || "Update failed", taskId, "due_date_change"); }
  }, [patchItem, toastError]);

  const handleDescriptionChange = useCallback(async (taskId, newDescription) => {
    if (!taskId) return;
    try {
      await updateWork({ task_id: taskId, description: newDescription });
      patchItem(taskId, { description: newDescription });
      setDetail((prev) => {
        if (!prev?.data) return prev;
        return { ...prev, data: { ...prev.data, task: { ...prev.data.task, description: newDescription } } };
      });
      toastSuccess("Description updated", taskId, "description_change");
    } catch (e) { toastError(e?.message || "Update failed", taskId, "description_change"); }
  }, [patchItem, toastSuccess, toastError]);

  const handleSlideOutTaskUpdated = useCallback(() => {
    setRetryNonce((n) => n + 1);
    setSubtaskRevision((r) => r + 1);
    // Reconcile the slide-out's detail silently (no skeleton) so attachments and
    // other server-side fields update in place. The list row refreshes via
    // retryNonce → useWorkBucketCache; SubtaskSection refetches via subtaskRevision.
    refreshDetailSilent();
  }, [refreshDetailSilent, setRetryNonce, setSubtaskRevision]);

  // ─── Slide-out delete (Item 3) — inline confirm, no modal ───────────────────
  // WorkPanel's confirm-on-second-click calls this with the task id. Returns
  // false on a user-facing refusal (effort logged / open commitment) so
  // WorkPanel keeps the slider open and resets its armed state; true on success.
  const handleSlideOutDelete = useCallback(async (id) => {
    if (!id) return false;
    try {
      const resp = await deleteWork(id);
      if (resp?.data?.error) {
        toastError(resp.data.message || "Delete failed", id, "delete");
        return false;
      }
      // Confirmed success — only now remove the task and close the slide-out.
      removeItem(id);
      setRetryNonce((n) => n + 1);
      setSelectedTask(null);
      // Toast with Undo (work-page-fixes §B): UndoToast → handleRestore →
      // fn_work_restore. Shown only on a confirmed-successful delete. This is
      // the sole success toast — no separate "Task deleted" toast, which
      // previously caused a double toast (the Undo toast already confirms
      // the deletion and offers restore).
      showUndoToast([id]);
      return true;
    } catch (err) {
      // Any backend refusal — including "this work was deleted … — restore it
      // first" on an already-soft-deleted row — is a real failure
      // (work-page-fixes §B): surface the backend message, keep the task in
      // the list, and keep the slide-out open (return false).
      toastError(err?.message || "Delete failed", id, "delete");
      return false;
    }
  }, [removeItem, setRetryNonce, setSelectedTask, showUndoToast, toastError]);

  // ─── Archive core (shared by handleArchive / handleRowArchive) ─────────────
  // archiveWork on an already-soft-deleted row raises the fn_work_delete
  // trigger notice ("this work was deleted …"). The row IS gone from the DB,
  // so treat it as already archived: run the same cleanup and do NOT show an
  // error. The kebab (row) path additionally offers Undo on that path.
  const archiveTask = useCallback(async (taskId, { showUndoOnAlreadyDeleted = false } = {}) => {
    const cleanupArchived = () => {
      removeItem(taskId);
      setDebouncedSearch("");
      setSearchQuery("");
      setRetryNonce((n) => n + 1);
    };
    try {
      await archiveWork(taskId);
      cleanupArchived();
    } catch (err) {
      if (isAlreadyDeletedError(err)) {
        cleanupArchived();
        if (showUndoOnAlreadyDeleted) showUndoToast([taskId]);
        return;
      }
      toastError(`Archive failed: ${err?.message || "Unknown"}`, taskId, "archive");
    }
  }, [removeItem, setDebouncedSearch, setSearchQuery, setRetryNonce, showUndoToast, toastError]);

  const handleArchive = useCallback(
    async (taskId) => archiveTask(taskId),
    [archiveTask]
  );

  const handleRowArchive = useCallback(
    async (item) => archiveTask(item.id, { showUndoOnAlreadyDeleted: true }),
    [archiveTask]
  );

  // ─── Kebab delete ─────────────────────────────────────────────────────────────
  const handleRowDelete = useCallback(async (item, reason) => {
    const cleanupDeleted = () => {
      removeItem(item.id);
      setDebouncedSearch("");
      setSearchQuery("");
      setRetryNonce((n) => n + 1);
    };
    try {
      const resp = await deleteWork(item.id, reason);
      if (resp?.data?.error) { toastError(resp.data.message || "Delete failed", item.id, "delete"); return; }
      // Confirmed success (work-page-fixes §B): remove the task and show the
      // toast with Undo (UndoToast → handleRestore → fn_work_restore).
      cleanupDeleted();
      showUndoToast([item.id]);
    } catch (err) {
      // Any backend refusal — including "this work was deleted … — restore it
      // first" on an already-soft-deleted row — is a real failure
      // (work-page-fixes §B): surface the backend message and keep the task
      // in the list.
      toastError(err?.message || "Delete failed", item.id, "delete");
    }
  }, [removeItem, setDebouncedSearch, setSearchQuery, setRetryNonce, showUndoToast, toastError]);

  return {
    detail,
    setDetail,
    refreshDetailSilent,
    handlePositionClick,
    handleTitleChange,
    handlePriorityChange,
    handleServiceChange,
    handleWorkTypeChange,
    handleClientChange,
    handleSlideOutDueDateChange,
    handleDescriptionChange,
    handleSlideOutTaskUpdated,
    handleSlideOutDelete,
    handleArchive,
    handleRowArchive,
    handleRowDelete,
  };
};

export default useWorkDetail;
